/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  CreaUfficioRequest,
  GetUfficiLiteParams,
  GetUfficioResponse,
  GetUfficiParams,
  GetUfficiRicorsioneParams,
  WsRispostaPaginataListGetUfficioResponse,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Aggiorna un ufficio
 * @summary Aggiorna un ufficio
 */
export const putUfficiIdUfficio = (
  idUfficio: string,
  creaUfficioRequest: CreaUfficioRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/uffici/${idUfficio}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: creaUfficioRequest,
    },
    options,
  );
};

export const getPutUfficiIdUfficioMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUfficiIdUfficio>>,
    TError,
    { idUfficio: string; data: CreaUfficioRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putUfficiIdUfficio>>,
  TError,
  { idUfficio: string; data: CreaUfficioRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putUfficiIdUfficio>>,
    { idUfficio: string; data: CreaUfficioRequest }
  > = props => {
    const { idUfficio, data } = props ?? {};

    return putUfficiIdUfficio(idUfficio, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutUfficiIdUfficioMutationResult = NonNullable<
  Awaited<ReturnType<typeof putUfficiIdUfficio>>
>;
export type PutUfficiIdUfficioMutationBody = CreaUfficioRequest;
export type PutUfficiIdUfficioMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna un ufficio
 */
export const usePutUfficiIdUfficio = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUfficiIdUfficio>>,
    TError,
    { idUfficio: string; data: CreaUfficioRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutUfficiIdUfficioMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca una serie di uffici. Ordinabili per tutti i campi (idUfficio, denominazione, codSuape, indirizzo, cap, telefono, fax, email, flAttivo, idUtenteResponsabile, flProtocollo, indirizzoPec, flInviaAllegati, dimensioneMaxAllegati, flPagamentoOnline, flPagamentoOffline, riversamentoAutomatico, flConservazione, flMarcatura, flRea, flUos, codiceAmministrazione, identificativoSuap, idUfficioEsterno, idArea, competenzaList, flEnteTerzo.
 * @summary Cerca una serie di uffici.
 */
export const getUffici = (
  params?: GetUfficiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListGetUfficioResponse>(
    { url: `/framework-cfg-utenti/uffici`, method: 'get', params, signal },
    options,
  );
};

export const getGetUfficiQueryKey = (params?: GetUfficiParams) =>
  [`/framework-cfg-utenti/uffici`, ...(params ? [params] : [])] as const;

export const getGetUfficiQueryOptions = <
  TData = Awaited<ReturnType<typeof getUffici>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUfficiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUffici>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getUffici>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUfficiQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUffici>>> = ({
    signal,
  }) => getUffici(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetUfficiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUffici>>
>;
export type GetUfficiQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di uffici.
 */
export const useGetUffici = <
  TData = Awaited<ReturnType<typeof getUffici>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUfficiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUffici>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUfficiQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Inserisce un ufficio
 * @summary Inserisce un ufficio
 */
export const postUffici = (
  creaUfficioRequest: CreaUfficioRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/uffici`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaUfficioRequest,
    },
    options,
  );
};

export const getPostUfficiMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUffici>>,
    TError,
    { data: CreaUfficioRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postUffici>>,
  TError,
  { data: CreaUfficioRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postUffici>>,
    { data: CreaUfficioRequest }
  > = props => {
    const { data } = props ?? {};

    return postUffici(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostUfficiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postUffici>>
>;
export type PostUfficiMutationBody = CreaUfficioRequest;
export type PostUfficiMutationError = ErrorType<unknown>;

/**
 * @summary Inserisce un ufficio
 */
export const usePostUffici = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postUffici>>,
    TError,
    { data: CreaUfficioRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostUfficiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce un ufficio
 * @summary Restituisce un ufficio
 */
export const getUfficiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<GetUfficioResponse>(
    { url: `/framework-cfg-utenti/uffici/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetUfficiIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/uffici/${id}`] as const;

export const getGetUfficiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getUfficiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUfficiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getUfficiId>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUfficiIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUfficiId>>> = ({
    signal,
  }) => getUfficiId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetUfficiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUfficiId>>
>;
export type GetUfficiIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce un ufficio
 */
export const useGetUfficiId = <
  TData = Awaited<ReturnType<typeof getUfficiId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUfficiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUfficiIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancella un ufficio
 * @summary Cancella un ufficio
 */
export const deleteUfficiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/uffici/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteUfficiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUfficiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteUfficiId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteUfficiId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteUfficiId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUfficiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteUfficiId>>
>;

export type DeleteUfficiIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella un ufficio
 */
export const useDeleteUfficiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUfficiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteUfficiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Può cercare gli uffici filtrando per denominazione (se passato un valore con almeno tre caratteri), idUfficioPadre (cerca sia l'ufficio con questo id che i suoi discendenti in maniera ricorsiva) e/o per uffici afferenti la competenza idCompetenza. Se nessuno dei tre filtri è settato allora la query non viene lanciata e si restituisce come set di dati una lista vuota
 * @summary Cerca una serie di uffici.
 */
export const getUfficiRicorsione = (
  params?: GetUfficiRicorsioneParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListGetUfficioResponse>(
    {
      url: `/framework-cfg-utenti/uffici/ricorsione`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetUfficiRicorsioneQueryKey = (
  params?: GetUfficiRicorsioneParams,
) =>
  [
    `/framework-cfg-utenti/uffici/ricorsione`,
    ...(params ? [params] : []),
  ] as const;

export const getGetUfficiRicorsioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getUfficiRicorsione>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUfficiRicorsioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUfficiRicorsione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getUfficiRicorsione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUfficiRicorsioneQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUfficiRicorsione>>
  > = ({ signal }) => getUfficiRicorsione(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetUfficiRicorsioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUfficiRicorsione>>
>;
export type GetUfficiRicorsioneQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di uffici.
 */
export const useGetUfficiRicorsione = <
  TData = Awaited<ReturnType<typeof getUfficiRicorsione>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUfficiRicorsioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUfficiRicorsione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUfficiRicorsioneQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API efficiente per cercare uno o più uffici. Ordinabile e Filtrabile per denominazione
 * @summary Cerca uno o più uffici. Ordinabile e Filtrabile per denominazione
 */
export const getUfficiLite = (
  params?: GetUfficiLiteParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListGetUfficioResponse>(
    { url: `/framework-cfg-utenti/uffici/lite`, method: 'get', params, signal },
    options,
  );
};

export const getGetUfficiLiteQueryKey = (params?: GetUfficiLiteParams) =>
  [`/framework-cfg-utenti/uffici/lite`, ...(params ? [params] : [])] as const;

export const getGetUfficiLiteQueryOptions = <
  TData = Awaited<ReturnType<typeof getUfficiLite>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUfficiLiteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUfficiLite>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getUfficiLite>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUfficiLiteQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUfficiLite>>> = ({
    signal,
  }) => getUfficiLite(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetUfficiLiteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUfficiLite>>
>;
export type GetUfficiLiteQueryError = ErrorType<unknown>;

/**
 * @summary Cerca uno o più uffici. Ordinabile e Filtrabile per denominazione
 */
export const useGetUfficiLite = <
  TData = Awaited<ReturnType<typeof getUfficiLite>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUfficiLiteParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUfficiLite>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUfficiLiteQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
